import { MessageId } from '@/models/Message';
import { mkPaperFinderChipTrackAttrs } from '@/analytics/props/inlineWidget';
import { openPaperFinderWidget } from '@/store/actionCreators/WidgetActions';
import { PaperFinderFeedbackButtons } from '@/components/chat/PaperFinderFeedback';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { setActiveWidgetDataForThread } from '@/store/reducers/PaperFinderWidgetStore';
import { ThreadId } from '@/models/Thread';
import { useAppDispatch } from '@/store/hooks';
import { WidgetId } from '@/models/PanelWidgets';

import { Box, Button, Divider } from '@mui/material';
import { Fragment, PropsWithChildren, useContext } from 'react';
import styled from 'styled-components';

type InlinePaperFinderWidgetProps = PropsWithChildren<{
  messageId: MessageId;
  threadId: ThreadId;
  widgetId: WidgetId;
}>;

export function InlinePaperFinderWidget(props: InlinePaperFinderWidgetProps) {
  const { messageId, threadId, widgetId, children } = props;
  const { isReadOnly } = useContext(PermissionsContext);

  const dispatch = useAppDispatch();

  const openPaperFinder = () => {
    // NOTE: although it might appear that both actions are redundnet, they
    // actually influance different state. One used directly by paper finder
    // to know which widget is in focus for a thread and the second controls
    // the state of the widget panel
    dispatch(
      setActiveWidgetDataForThread({ threadId, data: { id: widgetId } }),
    );
    dispatch(openPaperFinderWidget({ threadId, widgetId }));
  };

  return (
    <StyledBox>
      <PaddedBox>
        <ViewPapersButton
          onClick={openPaperFinder}
          size="small"
          {...mkPaperFinderChipTrackAttrs({ messageId, threadId, widgetId })}
        >
          {children}
        </ViewPapersButton>
      </PaddedBox>
      {!isReadOnly && (
        <Fragment>
          <Divider sx={{ borderColor: 'rgba(250,242,233,0.1)' }} />
          <PaddedBox>
            <PaperFinderFeedbackButtons
              messageId={messageId}
              threadId={threadId}
              widgetId={widgetId}
            />
          </PaddedBox>
        </Fragment>
      )}
    </StyledBox>
  );
}

const StyledBox = styled(Box)`
  background-color: ${({ theme }) => theme.color['dark-teal-100'].hex};
  border-radius: 7px;
`;

const PaddedBox = styled(Box)`
  padding: ${({ theme }) => theme.spacing(1)};
`;

const ViewPapersButton = styled(Button)`
  &.MuiButton-root {
    color: ${({ theme }) => theme.palette.secondary.main};
    font-size: 1rem;
    padding: 0;
  }
`;
